import { ParseStepResult } from "papaparse";

import { Country } from "@models/Country";
import { State } from "@models/State";

const emailListColumn = "Emails (separated by a semicolon)";
const territoryListColumn = "Territory (separated by a semicolon)";

export const bulkAddressColumns = (addressBookType: "user" | "territory") => [
  "Name",
  "Street Address One",
  "Street Address Two",
  "Attention",
  "City",
  "State / Province",
  "Region",
  "Zip",
  "Country",
  "Phone Number",
  addressBookType === "user" ? emailListColumn : territoryListColumn,
];

type ValidAddress = {
  name: string;
  streetAddress1: string;
  streetAddress2: string | null;
  defaultAttn: string | null;
  city: string;
  stateId: number | null;
  zip: string;
  countryId: number;
  phoneNumber: string;
  region: string | null;
  emailList?: string[];
  territoryList?: string[];
};

type CSVRow = {
  Name: string;
  "Street Address One": string;
  "Street Address Two"?: string;
  Attention?: string;
  City: string;
  "State / Province": string;
  Zip: string;
  Country: string;
  "Phone Number": string;
  Region?: string;
  [emailListColumn]: string;
  [territoryListColumn]: string;
  error?: string;
  line?: number;
  stateId?: number;
  countryId?: number;
};

const parseArray = (arr: string | undefined) => {
  if (!arr) return [];
  return arr
    .split(";")
    .map((item) => item.trim())
    .filter(Boolean);
};

const lineError = (line: number, error: string) =>
  `Line: ${line}, Error: ${error}`;

const getStateId = (state: string, stateList: State[]) => {
  const stateCode = state.trim().toLowerCase();
  const foundState = stateList.find((s) => s?.code.toLowerCase() === stateCode);
  return foundState ? parseInt(foundState.id) : null;
};

const getCountryId = (country: string, countryList: Country[]) => {
  const countryName = country.trim().toLowerCase();
  const foundCountry = countryList.find(
    (c) => c.name.toLowerCase() === countryName
  );
  return foundCountry ? parseInt(foundCountry.id) : null;
};

export const handleAddressCSV = (
  file: ParseStepResult<CSVRow>[],
  states: State[],
  countries: Country[],
  addressBookType: "user" | "territory"
): { validData: ValidAddress[]; errors: string[] } => {
  const csvErrors = file.map((dp) => dp.errors.map((e) => e.message)).flat();

  if (csvErrors.length > 0) {
    return { validData: [], errors: csvErrors };
  }

  const requiredFields = [
    "Name",
    "Street Address One",
    "City",
    "Zip",
    "Phone Number",
    "Country",
    addressBookType === "user" ? emailListColumn : territoryListColumn,
  ];

  const columnErrors: string[] = [];
  // Check if all required fields are present in the file
  const fieldsFoundInData = Object.keys(file[0].data);
  for (const column of requiredFields) {
    if (!fieldsFoundInData.includes(column)) {
      columnErrors.push(`Missing required column headers: ${column}`);
    }
  }

  if (columnErrors.length > 0) {
    return { validData: [], errors: columnErrors };
  }

  // Type assertion since we validated columns exist
  const data = file.map((dp) => dp.data as CSVRow);

  const errors: string[] = [];

  const mappedData = data.map((dataPoint, i) => {
    const missingRequiredFields = requiredFields.filter(
      (field) => dataPoint[field].trim().length === 0
    );
    if (missingRequiredFields.length > 0) {
      errors.push(
        lineError(
          i,
          "Missing required fields: " + missingRequiredFields.join(", ")
        )
      );
    }

    const region = dataPoint.Region?.trim() || null;
    const stateId = getStateId(dataPoint["State / Province"], states);
    if (!region && !stateId) {
      errors.push(
        lineError(i, "Invalid state: " + dataPoint["State / Province"])
      );
    }

    const countryId = getCountryId(dataPoint.Country, countries);
    if (!countryId) {
      errors.push(lineError(i, "Invalid country: " + dataPoint.Country));
    }

    const zip = dataPoint.Zip.trim();
    if (countryId === 1 && zip.length !== 5) {
      errors.push(
        lineError(
          i,
          "Invalid zip code: " + zip + " (USA zip codes are 5 digits)"
        )
      );
    }

    const emailList = parseArray(dataPoint[emailListColumn]);
    const terrList = parseArray(dataPoint[territoryListColumn]);
    return {
      name: dataPoint.Name.trim(),
      streetAddress1: dataPoint["Street Address One"].trim(),
      streetAddress2: dataPoint["Street Address Two"]?.trim() || null,
      defaultAttn: dataPoint.Attention?.trim() || null,
      city: dataPoint.City.trim(),
      stateId: stateId,
      zip: zip,
      countryId: countryId,
      phoneNumber: dataPoint["Phone Number"].trim(),
      region: region,
      ...(addressBookType === "user" && {
        emailList: emailList,
      }),
      ...(addressBookType === "territory" && {
        territoryList: terrList,
      }),
    };
  });

  if (errors.length > 0) {
    return { validData: [], errors: errors };
  }

  return { validData: mappedData as ValidAddress[], errors: [] };
};
